import {sharedCampaigns} from '@cl/shared';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {CreativeCardScratchpadState} from '../../scratchpad/creativeCardScratchpad.types';
import validateCreativeToBeUsedAsFallbackCreative from '../../utils/creative/validateCreativeToBeUsedAsFallbackCreative';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setIsFallbackInputSchema = z.object({
  isFallback: z.boolean().describe('Whether this creative card is a fallback creative'),
});

function cardHasInputConnections(cardId: number): boolean {
  const node = sharedCampaigns.nodesRef.value[cardId];

  return Boolean(node?.inputs && node.inputs.length > 0);
}

export default function setIsFallbackTool() {
  async function execute(
    params: ToolExecuteParams<typeof setIsFallbackInputSchema, CreativeCardScratchpadState>,
  ) {
    const {isFallback} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<CreativeCardScratchpadState>(
      agentScratchpad,
      NodeType.CREATIVE,
    );

    if (isFallback) {
      if (validatedScratchpad.card.isFallbackCreative) {
        return toolResult('The creative card is already marked as a fallback creative.');
      }

      const cardId = validatedScratchpad.card.id;

      if (cardId > NO_NODE_ID && cardHasInputConnections(cardId)) {
        const node = sharedCampaigns.nodesRef.value[cardId];
        const inputCount = node?.inputs?.length ?? 0;

        return toolError(
          `Only a non-attached creative card can be set as a fallback creative card. This card currently receives inputs from ${inputCount} other card(s). Please detach the card first if you want to turn it into a fallback creative card.`,
        );
      }

      const creative = validatedScratchpad.card.creative;

      if (creative) {
        const fallbackValidation = validateCreativeToBeUsedAsFallbackCreative(creative);

        if (fallbackValidation.isValid === false) {
          return toolError(fallbackValidation.errorMessage);
        }
      }
    }

    validatedScratchpad.card.isFallbackCreative = isFallback;

    return toolResult(
      isFallback
        ? 'Creative card set as fallback creative.'
        : 'Creative card is no longer a fallback creative.',
    );
  }

  return tool<typeof setIsFallbackInputSchema, CreativeCardScratchpadState>({
    name: V2CardAgentToolName.SetIsFallback,
    displayName: 'Setting fallback creative',
    description:
      'Set whether this creative card is a fallback creative. Fallback creatives cannot be connected to other cards and must not use products, location, content, or weather. Only non-attached cards can be set as fallback.',
    inputSchema: setIsFallbackInputSchema,
    execute,
  });
}
